/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { WizardFormErrors } from '@futrli/ui-components/components/sidebar/wizard/types';

import { NewConsolidatedForecastDataSource } from '../../dashboard/types';
import { ForecastDatasourceProps } from '../components/form-elements/forecast-datasource';
import { ForecastDurationProps } from '../components/form-elements/forecast-duration';
import { ForecastFromProps } from '../components/form-elements/forecast-from';
import { ForecastNameProps } from '../components/form-elements/forecast-name';
import { ForecastSourceForecastProps } from '../components/form-elements/forecast-source-forecast';
import { getCreateConsolidatedForecastRequestData } from '../get-create-consolidated-forecast-request-data';
import { ForecastFormElementIds, ForecastKindOption } from '../types';

interface ConsolidatedForcastFormInitialValues {
  datasource: NewConsolidatedForecastDataSource | '';
  duration: string;
  from: string;
  name: string;
  sourceForecast: string;
  sourceSubEntityForecasts: Record<string, string>;
}

const dateRegex = /^\d\d\/\d\d\/\d\d\d\d$/; // test for DD/MM/YYYY

export const useCreateConsolidatedForecastForm = (
  kind: ForecastKindOption,
  initialValues: Partial<ConsolidatedForcastFormInitialValues> = {}
) => {
  const [datasource, setDataSource] = useState<NewConsolidatedForecastDataSource | ''>(initialValues.datasource ?? '');
  const [duration, setDuration] = useState(initialValues.duration ?? '');
  const [errors, setErrors] = useState<WizardFormErrors>({});
  const [from, setFrom] = useState(initialValues.from ?? '');
  const [name, setName] = useState(initialValues.name ?? '');
  const [sourceForecast, setSourceForecast] = useState(initialValues.sourceForecast ?? '');
  const [sourceSubEntityForecasts, setSourceSubEntityForecasts] = useState(
    initialValues.sourceSubEntityForecasts ?? {}
  );

  const intl = useIntl();
  const hasBeenValidated = useRef(false);

  const getFormData = useCallback(() => {
    return {
      datasource,
      duration,
      from,
      name,
      sourceForecast,
      sourceSubEntityForecasts
    };
  }, [datasource, duration, from, name, sourceForecast, sourceSubEntityForecasts]);

  const validate = useCallback(
    (newData = getFormData()) => {
      const newErrors: WizardFormErrors = {};
      const { datasource, duration, from, name, sourceForecast, sourceSubEntityForecasts } = newData;

      // Name
      if (!name.trim().length) {
        if (kind === 'budget') {
          newErrors.name = {
            id: ForecastFormElementIds.Name,
            message: intl.formatMessage({
              id: 'predict.create_forecast.validation.budget_name_is_required',
              defaultMessage: 'Budget name is required'
            })
          };
        } else {
          newErrors.name = {
            id: ForecastFormElementIds.Name,
            message: intl.formatMessage({
              id: 'predict.create_forecast.validation.forecast_name_is_required',
              defaultMessage: 'Forecast name is required'
            })
          };
        }
      }

      // From
      if (!from.trim().length) {
        newErrors.from = {
          id: ForecastFormElementIds.From,
          message: intl.formatMessage({
            id: 'predict.create_forecast.validation.start_date_is_required',
            defaultMessage: 'Start date is required'
          })
        };
      } else if (from.trim().length && !dateRegex.test(from.trim())) {
        newErrors.from = {
          id: ForecastFormElementIds.From,
          message: intl.formatMessage({
            id: 'predict.create_forecast.validation.enter_valid_date_format',
            defaultMessage: 'Enter valid date format (DD/MM/YYYY)'
          })
        };
      }

      if (kind === 'scenario' && !duration) {
        newErrors.duration = {
          id: ForecastFormElementIds.Duration,
          message: intl.formatMessage({
            id: 'predict.create_forecast.validation.please_select_an_option',
            defaultMessage: 'Please select an option'
          })
        };
      }

      // Datasource
      if (!datasource) {
        newErrors.datasource = {
          id: ForecastFormElementIds.Datasource,
          message: intl.formatMessage({
            id: 'predict.create_forecast.validation.please_select_an_option',
            defaultMessage: 'Please select an option'
          })
        };
      } else if (datasource === NewConsolidatedForecastDataSource.CREATE_FROM_EXISTING) {
        if (!sourceForecast.trim().length) {
          if (kind === 'budget') {
            newErrors.sourceForecast = {
              id: ForecastFormElementIds.SourceForecast,
              message: intl.formatMessage({
                id: 'predict.create_forecast.validation.you_must_select_a_budget_to_copy',
                defaultMessage: 'You must select a budget to copy'
              })
            };
          } else {
            newErrors.sourceForecast = {
              id: ForecastFormElementIds.SourceForecast,
              message: intl.formatMessage({
                id: 'predict.create_forecast.validation.you_must_select_a_forecast_to_copy',
                defaultMessage: 'You must select a forecast to copy'
              })
            };
          }
        }
      }

      // Sub-entity forecasts datasource option validation
      if (datasource === NewConsolidatedForecastDataSource.CREATE_FROM_EXISTING_SUB_ENTITY_FORECASTS) {
        Object.keys(sourceSubEntityForecasts).forEach(orgId => {
          if (!sourceSubEntityForecasts[orgId]) {
            newErrors[`sub-entity-forecast-${orgId}`] = {
              id: `sub-entity-forecast-${orgId}`,
              message: intl.formatMessage({
                id: 'predict.create_forecast.validation.you_must_select_a_forecast',
                defaultMessage: 'You must select a forecast'
              })
            };
          }
        });

        if (!Object.keys(sourceSubEntityForecasts).length) {
          newErrors.datasource = {
            id: ForecastFormElementIds.Datasource,
            message: intl.formatMessage({
              id: 'predict.create_forecast.validation.you_must_select_a_forecast',
              defaultMessage: 'You must select a forecast'
            })
          };
        }
      }

      setErrors(newErrors);
      hasBeenValidated.current = true;
      return Object.values(newErrors).length === 0;
    },
    [getFormData, intl, kind]
  );

  const revalidate = useCallback(
    (newData = getFormData()) => {
      if (!hasBeenValidated.current) {
        return true;
      }
      return validate(newData);
    },
    [getFormData, validate]
  );

  const onChangeDatasource: ForecastDatasourceProps['onChange'] = useCallback(
    (e: any) => {
      setDataSource(e.target.value);
      revalidate({ ...getFormData(), datasource: e.target.value });
    },
    [getFormData, revalidate]
  );

  const onChangeDuration: ForecastDurationProps['onChange'] = useCallback(
    (e: any) => {
      setDuration(e.target.value);
      revalidate({ ...getFormData(), duration: e.target.value });
    },
    [getFormData, revalidate]
  );

  const onChangeFrom: ForecastFromProps['onChange'] = useCallback(
    (e: any) => {
      setFrom(e.target.value.formattedValue);
      revalidate({ ...getFormData(), from: e.target.value.formattedValue });
    },
    [getFormData, revalidate]
  );

  const onChangeName: ForecastNameProps['onChange'] = useCallback(
    (e: any) => {
      setName(e.target.value);
      revalidate({ ...getFormData(), name: e.target.value });
    },
    [getFormData, revalidate]
  );

  const onChangeSourceForecast: ForecastSourceForecastProps['onChange'] = useCallback(
    (e: any) => {
      setSourceForecast(e.target.value);
      revalidate({ ...getFormData(), sourceForecast: e.target.value });
    },
    [getFormData, revalidate]
  );

  const onChangeSourceSubEntityForecast = useCallback(
    (orgId: string, forecastId: string) => {
      const newData = { ...getFormData() };
      newData.sourceSubEntityForecasts = { ...newData.sourceSubEntityForecasts, [orgId]: forecastId };
      setSourceSubEntityForecasts(newData.sourceSubEntityForecasts);
      revalidate(newData);
    },
    [getFormData, revalidate]
  );

  const onChangeSourceSubEntity = useCallback(
    (orgId: string) => {
      const newData = { ...getFormData() };
      newData.sourceSubEntityForecasts = { ...newData.sourceSubEntityForecasts };
      if (typeof newData.sourceSubEntityForecasts[orgId] !== 'undefined') {
        delete newData.sourceSubEntityForecasts[orgId];
      } else {
        newData.sourceSubEntityForecasts[orgId] = '';
      }
      setSourceSubEntityForecasts(newData.sourceSubEntityForecasts);
      revalidate(newData);
    },
    [getFormData, revalidate]
  );

  const getRequestData = useCallback(() => {
    return getCreateConsolidatedForecastRequestData({
      datasource: datasource as NewConsolidatedForecastDataSource,
      duration,
      from,
      kind,
      name,
      sourceForecast,
      sourceSubEntityForecasts
    });
  }, [datasource, duration, from, kind, name, sourceForecast, sourceSubEntityForecasts]);

  return {
    datasource,
    duration,
    errors,
    from,
    name,
    sourceForecast,
    sourceSubEntityForecasts,
    getRequestData,
    onChangeDatasource,
    onChangeDuration,
    onChangeFrom,
    onChangeName,
    onChangeSourceForecast,
    onChangeSourceSubEntityForecast,
    onChangeSourceSubEntity,
    revalidate,
    setDuration,
    setFrom,
    validate
  };
};
